/**
 * Credential Service
 * Helper functions for accessing WebAuthn passkey credentials
 *
 * Note: The Dexie table and type retain the "biometric" name because renaming
 * them would require a database migration. The public API uses "passkey".
 */

import db from '@/db';
import { BiometricCredential } from '../types/biometric_credential';

/**
 * Get the passkey credential for the current user
 * @returns BiometricCredential or null if not registered
 */
export async function getPasskeyCredential(): Promise<BiometricCredential | null> {
  const credentials = await db.biometricCredentials.toArray();
  return credentials.length > 0 ? credentials[0] : null;
}

/**
 * Save a passkey credential to the database
 * @param credential - The passkey credential to save
 */
export async function savePasskeyCredential(credential: BiometricCredential): Promise<void> {
  await db.biometricCredentials.put(credential);
}

/**
 * Check if passkey authentication is registered
 * @returns true if passkey credential exists
 */
export async function isPasskeyRegistered(): Promise<boolean> {
  const count = await db.biometricCredentials.count();
  return count > 0;
}

/**
 * Clear passkey credential from the database
 * This does NOT revoke the credential from the authenticator
 */
export async function clearPasskeyCredential(): Promise<void> {
  await db.biometricCredentials.clear();
}
